import { driver, DeeplinkActionHandler } from "@leagueplatform/masonry";
import { APP_MANAGED_ROUTES } from "common/app-managed-routes";
import { resolveCanonicalEntryPoint } from "live-chat/live-chat-entry-point";

// The BE emits some deeplink paths without a leading slash (e.g.
// `sso/vendors/emblem?vendorId=…`); the stock `DeeplinkActionHandler` drops
// those silently. Prepend `/` for relative non-scheme URLs before delegating.
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

// Match the way React Router matches `/live-chat`: case-insensitive and
// ignoring trailing slashes. `/live-chat/pre-chat` still doesn't match.
const normalizeRoutePath = (pathname: string) =>
  pathname.replace(/\/+$/, "").toLowerCase();

// EMBLEM-7820: an in-app tap on an AI `/live-chat` deeplink (Journey ›
// Explore Health Coach tile) used to navigate to `LiveChatEntryRoute`,
// which redirects to /home — the member lost their page. Open the panel
// in place instead. Human entries still navigate so pre-chat can supply
// the HOO gate + routing config.
const isInPlaceLiveChatDeeplink = (url: string) => {
  const { pathname, searchParams } = new URL(url, window.location.origin);
  return (
    normalizeRoutePath(pathname) ===
      normalizeRoutePath(APP_MANAGED_ROUTES.LIVE_CHAT) &&
    resolveCanonicalEntryPoint(searchParams.get("entryPoint")) !== "human"
  );
};

export const registerNormalizingDeeplinkHandler = () => {
  driver.actionController.registerUniversalHandler(
    "deeplink",
    async (action, resources) => {
      const url = action.payload?.url;
      if (
        typeof url === "string" &&
        url.length > 0 &&
        !url.startsWith("//") &&
        !SCHEME_RE.test(url)
      ) {
        const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
        if (isInPlaceLiveChatDeeplink(normalizedUrl)) {
          action.event?.preventDefault();
          // Dynamic import keeps `@leagueplatform/chat`'s module-load
          // renderer registrations out of bootstrap (same as the
          // `openLiveChat` handler in `register-emblem-actions.tsx`).
          const { openChatSession } = await import("live-chat/chat-instance");
          openChatSession();
          return { isSuccess: true };
        }
        if (normalizedUrl !== url) {
          return DeeplinkActionHandler(
            { ...action, payload: { ...action.payload, url: normalizedUrl } },
            resources
          );
        }
      }
      return DeeplinkActionHandler(action, resources);
    },
    true
  );
};
